/**
 * Admin PWA - Main Application Entry
 * 
 * Merchant dashboard for restaurant management:
 * - Menu management
 * - Staff & scheduling
 * - Reports & analytics
 * - Settings & configuration
 * - Inventory management
 * - Multi-location support
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { pwaPath } from '@/utils/getBasePath';
import { Toaster } from 'sonner';
import { LayoutDashboard } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { PwaAccountGate } from '@/components/pwa/PwaAccountGate';

// Pages
import DashboardPage from './pages/DashboardPage';
import MenuImportPage from './pages/MenuImportPage';
import UnifiedMenuManagement from '../../pages/backoffice/UnifiedMenuManagement';
import StaffPage from './pages/StaffPage';
import SchedulePage from './pages/SchedulePage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import CycleCountPage from './pages/CycleCountPage';
import CycleCountDetailPage from '@/pages/backoffice/inventory/CycleCountDetailPage';
import OrdersPage from './pages/OrdersPage';
import AppLinksPage from './pages/AppLinksPage';
import PaymentConfigurationPage from './pages/PaymentConfigurationPage';

// Context providers
import { AuthProvider } from './context/AuthContext';
import { BranchProvider } from './context/BranchContext';

// The one PWA restaurant-account sign-in (components/pwa) in front of every page.
export default function AdminApp() {
  return (
    <>
    <Toaster position="top-right" richColors />
    <PwaAccountGate appName="Admin Dashboard" icon={LayoutDashboard} accent="#10b981">
      {(account) => (
    <AuthProvider key={`${account.restaurant.id}:${account.signedInAt}`}>
      <BranchProvider>
        <Routes>
          {/* the sign-in screen is the gate; an old /login link lands on the dashboard */}
          <Route path="login" element={<Navigate to="../dashboard" replace />} />
          
          <Route
            path="/*"
            element={
                <AdminLayout>
                  <Routes>
                    <Route index element={<DashboardPage />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    
                    {/* Menu */}
                    <Route path="menu" element={<UnifiedMenuManagement />} />
                    <Route path="menu/import" element={<MenuImportPage />} />
                    
                    {/* Staff */}
                    <Route path="staff" element={<StaffPage />} />
                    <Route path="staff/schedule" element={<SchedulePage />} />
                    
                    {/* Orders */}
                    <Route path="orders" element={<OrdersPage />} />
                    
                    {/* Inventory */}
                    <Route path="inventory" element={<Navigate to="cycle-count" replace />} />
                    <Route path="inventory/cycle-count" element={<CycleCountPage />} />
                    <Route path="inventory/cycle-count/:taskId" element={<CycleCountDetailPage />} />
                    
                    {/* Reports */}
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="reports/:type" element={<ReportsPage />} />
                    
                    {/* Settings */}
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="settings/:section" element={<SettingsPage />} />
                    
                    {/* Payment Configuration */}
                    <Route path="payment-config" element={<PaymentConfigurationPage />} />
                    
                    {/* App Links */}
                    <Route path="apps" element={<AppLinksPage />} />
                    
                    {/* Catch all */}
                    <Route path="*" element={<Navigate to={pwaPath('admin', 'dashboard')} replace />} />
                  </Routes>
                </AdminLayout>
            }
          />
        </Routes>
      </BranchProvider>
    </AuthProvider>
      )}
    </PwaAccountGate>
    </>
  );
}
